import { getThreeLines } from 'icons'
import type { ChangeEvent, CSSProperties } from 'react'
import styled, { css } from 'styled-components'

export type Props = {
  id: string
  min: number
  max: number
  step: number
  value: number
  label: string
  displayScale?: boolean
  onChange: (value: number) => void
  disabled?: boolean
}

export default function RangeSlider({
  id,
  min = 0,
  max = 1,
  step = 0.1,
  value = 0,
  label,
  displayScale = false,
  onChange,
  ...props
}: Props) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.valueAsNumber)
  }

  const style = {
    width: '100%',
    '--thumbSize': '20px',
    '--ratio': (value - min) / (max - min)
  } as CSSProperties

  return (
    <Wrapper {...props} style={style}>
      <Tooltip htmlFor={id}>{label ? `${value} ${label}` : value}</Tooltip>

      <Input
        id={id}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        {...props}
      />

      {displayScale && (
        <Scale>
          <span>
            {min}
            {label}
          </span>
          <span>
            {max}
            {label}
          </span>
        </Scale>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const Tooltip = styled.output`
  --color: var(--white);
  --background: var(--dark-gray);

  .dark & {
    --color: var(--dark-gray);
    --background: var(--white);
  }

  display: flex;
  align-items: center;
  justify-content: center;

  width: 90px;
  height: 35px;
  padding: 6px;

  position: relative;
  left: calc((var(--thumbSize) / 2) + (var(--ratio) * 100%) - (var(--ratio) * var(--thumbSize)));
  transform: translate(-50%);

  background: var(--background);
  border-radius: 4px;

  color: var(--color);
  font: var(--600-13);

  ::after {
    content: '';
    position: absolute;
    bottom: -3px;
    width: 8px;
    height: 8px;
    border-radius: 1px;
    transform: rotate(45deg) skew(10deg, 10deg);
    background: var(--background);
  }
`

const thumb = css`
  width: var(--thumbSize);
  height: var(--thumbSize);

  background: white url(${getThreeLines()}) no-repeat center;
  border: 2px solid var(--green);
  border-radius: 50%;
  box-shadow: 0 0 0 5px var(--green_0-2);

  box-sizing: inherit;
  appearance: none;
  cursor: pointer;
`

const Input = styled.input.attrs({ type: 'range' })`
  height: 8px;
  margin: 0;

  background: linear-gradient(
    to right,
    var(--green) calc(var(--ratio) * 100%),
    var(--light-gray) 0
  );
  border-radius: 8px;

  appearance: none;

  :focus {
    outline: none;
  }

  ::-webkit-slider-runnable-track {
    cursor: pointer;
  }

  ::-moz-range-track {
    cursor: pointer;
  }

  ::-webkit-slider-thumb {
    ${thumb}
  }

  ::-moz-range-thumb {
    ${thumb}
  }

  ::-moz-focus-outer {
    border: none;
  }
`

const Scale = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  font: var(--600-12);
  color: var(--gray);
`
